import React from "react";
import Link from "next/link";

interface ErrorWithLink {
    message: string;
    clickableText: string;
    linkHref: string;
}

interface AppError {
    message: string;
}

interface NotificationHandler {
    error: (message: string | React.ReactNode) => void;
    success?: (message: string | React.ReactNode) => void;
    warning?: (message: string | React.ReactNode) => void;
    info?: (message: string | React.ReactNode) => void;
}

const ERROR_CONFIGS = {
    export: {
        defaultMessage:
            "Unfortunately, we encountered an error when processing the export request.",
        linkMappings: {
            inventory: {
                clickableText: "cost basis",
                linkHref: "/inventory",
            },
            "gnl-accounts": {
                clickableText: "account types",
                linkHref: "/gnl-accounts",
            },
        },
    },
} as const;

type ErrorType = keyof typeof ERROR_CONFIGS;

const extractErrorLink = (
    message: string,
    linkMappings: Record<string, { clickableText: string; linkHref: string }>,
): ErrorWithLink | null => {
    const urlMatch = /(https?:\/\/[^\s]+)/.exec(message);
    if (!urlMatch) return null;

    const url = urlMatch[1];
    const messageWithoutUrl = message.replace(/https?:\/\/[^\s]+/, "").trim();

    const linkConfig = Object.entries(linkMappings).find(([key]) =>
        url.includes(key),
    );

    if (!linkConfig) return null;

    const [, config] = linkConfig;
    return {
        message: messageWithoutUrl,
        clickableText: config.clickableText,
        linkHref: config.linkHref,
    };
};

const createErrorWithLink = (errorInfo: ErrorWithLink): React.ReactNode => {
    const { message, clickableText, linkHref } = errorInfo;
    const parts = message.split(clickableText);

    if (parts.length !== 2) {
        return message;
    }

    return (
        <div>
            {parts[0]}
            <Link href={linkHref} className="text-primary hover:underline">
                {clickableText}
            </Link>
            {parts[1]}
        </div>
    );
};

export const handleErrorMessage = (
    error: AppError,
    notify: NotificationHandler,
    errorType: ErrorType,
): void => {
    const config = ERROR_CONFIGS[errorType];
    const message = error.message || config.defaultMessage;

    const errorLink = extractErrorLink(message, config.linkMappings);

    if (errorLink) {
        const errorComponent = createErrorWithLink(errorLink);
        notify.error(errorComponent);
    } else {
        notify.error(message);
    }
};
